import React, { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApplicationRoleApplications, useTargetPerson } from 'hooks'
import { ApplicationsList, ItemDetails, TCodesList } from 'components'
import useSubcomponents from 'useSubcomponents'
import cartHelpers from 'containers/Cart/cartHelpers'
import { OverviewSection } from './OverviewSection'
import { MoreInformationSection } from './MoreInformationSection'
import { Box } from '@mui/material'
import { ResourceTypesNamespace, useRegistry } from 'core'
import { AddToCart } from 'components/AddToCart'
import ActivateNowButton from '../ActivateNowButton'
import useConfiguration from 'useConfiguration'
const applicationRoleDetailsTabExplain = {
    overview:
        'Overview tab: technical name, resource system, process, classification, and description for this application role.',
    additionalInformation:
        'More information tab: owners, approvers, responsible party, and local sensitive functions.',
    tCodes:
        'T-Codes tab: search and browse transaction codes linked to this application role.',
    applications:
        'Applications tab: search and browse applications that use this role.',
}

const ApplicationRoleDetails = ({
    applicationRole,
    assignmentStatus,
    toggleDrawer,
}) => {
    const { t } = useTranslation()
    const { getResourceType } = useConfiguration()
    const currentResourceType = getResourceType('ApplicationRoles')

    const registry = useRegistry()

    const applicationRoleType = registry.get(
        ResourceTypesNamespace,
        'ApplicationRoles',
    )

    const { hasAccessToTCodesGrid, canSeeApplications } = useSubcomponents()

    const [targetPerson] = useTargetPerson()

    const [activeTab, setActiveTab] = useState('overview')

    const preAdd = (baseObj) => {
        const itemToAdd = cartHelpers.appRoleToCartItem({
            targetPerson,
            assignmentType: 'Add',
            applicationRole,
            assignmentStatus,
            ...baseObj,
        })

        if (!itemToAdd) return undefined

        return itemToAdd
    }

    const getPreValidationMessage = () => {
        if (assignmentStatus && assignmentStatus.isAssigned) {
            return t('ResourceAlreadyAssigned')
        }
        return ''
    }

    return (
        <Fragment>
            <ItemDetails.Tabs
                variant="standard"
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
            >
                <ItemDetails.Tabs.Tab
                    value="overview"
                    label={t('Common_Overview')}
                    data-ai-explain={
                        applicationRoleDetailsTabExplain.overview
                    }
                />
                <ItemDetails.Tabs.Tab
                    value="additionalInformation"
                    label={t('Common_MoreInformation')}
                    data-ai-explain={
                        applicationRoleDetailsTabExplain.additionalInformation
                    }
                />
                {hasAccessToTCodesGrid && (
                    <ItemDetails.Tabs.Tab
                        value="tCodes"
                        label={t('Common_TCodes')}
                        data-protectedsubcomponent={hasAccessToTCodesGrid}
                        data-ai-explain={
                            applicationRoleDetailsTabExplain.tCodes
                        }
                    />
                )}

                {canSeeApplications && (
                    <ItemDetails.Tabs.Tab
                        value="applications"
                        label={t('Common_Applications')}
                        data-protectedsubcomponent={canSeeApplications}
                        data-ai-explain={
                            applicationRoleDetailsTabExplain.applications
                        }
                    />
                )}
            </ItemDetails.Tabs>
            {activeTab === 'overview' && (
                <OverviewSection applicationRole={applicationRole} />
            )}
            {activeTab === 'additionalInformation' && (
                <MoreInformationSection applicationRole={applicationRole} />
            )}
            {activeTab === 'tCodes' && (
                <Box
                    data-ai-explain="T-Codes linked to this application role: use search to filter the grid and pagination to move through results."
                >
                    <TCodesList resourceId={applicationRole.id} />
                </Box>
            )}
            {activeTab === 'applications' && (
                <Box
                    data-ai-explain="Applications using this role: search the list and use pagination when many applications are returned."
                >
                    <ApplicationsList
                        resource={applicationRole}
                        useDataHook={useApplicationRoleApplications}
                    />
                </Box>
            )}
            {applicationRole.canActivateNow ? (
                <ActivateNowButton
                    resource={applicationRole}
                    resourceType={currentResourceType}
                    forDetailsDrawer={true}
                    width="300px"
                />
            ) : (
                <Box
                    data-ai-explain="Add this application role to the shopping cart to submit an access request (subject to policy and validation)."
                >
                    <AddToCart
                        resourceType={applicationRoleType}
                        resource={applicationRole}
                        preAdd={preAdd}
                        postAdd={toggleDrawer}
                        preValidate={getPreValidationMessage}
                    />
                </Box>
            )}
        </Fragment>
    )
}

export default ApplicationRoleDetails
